export interface CodeSnippet {
  id: string;
  title: string;
  description: string;
  icon: string;
  iconAlt: string;
  tags: string[];
  code: string;
  language: string;
}

export const codeSnippets: CodeSnippet[] = [
  {
    id: 'rag-pipeline',
    title: 'Advanced RAG Pipeline with Vector Search & LLM Orchestration',
    description:
      'Production-grade Retrieval-Augmented Generation system with semantic chunking, hybrid search, and multi-agent workflows',
    icon: '/images/code/n8n.svg',
    iconAlt: 'n8n',
    tags: ['RAG', 'Vector DB', 'LangChain', 'OpenAI', 'Pinecone', 'Redis'],
    language: 'typescript',
    code: `// Advanced RAG Pipeline with Multi-Agent Orchestration
import { OpenAI } from "openai";
import { PineconeStore } from "@langchain/pinecone";
import { OpenAIEmbeddings } from "@langchain/openai";
import { RecursiveCharacterTextSplitter } from "langchain/text_splitter";
import { createClient } from "redis";
import { z } from "zod";

interface RAGConfig {
  vectorStore: PineconeStore;
  llm: OpenAI;
  cache: ReturnType<typeof createClient>;
  embeddingModel: OpenAIEmbeddings;
}

class AdvancedRAGPipeline {
  private config: RAGConfig;
  private semanticRouter: SemanticRouter;
  private queryRewriter: QueryRewriter;

  constructor(config: RAGConfig) {
    this.config = config;
    this.semanticRouter = new SemanticRouter(config.embeddingModel);
    this.queryRewriter = new QueryRewriter(config.llm);
  }

  async processQuery(query: string, context?: AgentContext): Promise<RAGResponse> {
    // Multi-step query processing with semantic routing
    const routingResult = await this.semanticRouter.route(query);
    const rewrittenQueries = await this.queryRewriter.expandQuery(query, routingResult);
    
    // Parallel retrieval with fusion scoring
    const retrievalPromises = rewrittenQueries.map(async (q) => {
      const results = await this.hybridSearch(q);
      return this.rankAndScore(results, query);
    });
    
    const retrievalResults = await Promise.all(retrievalPromises);
    const fusedResults = this.fuseRetrievalResults(retrievalResults);
    
    // Multi-agent response generation
    return await this.generateResponse(query, fusedResults, context);
  }

  private async hybridSearch(query: string): Promise<Document[]> {
    // Combine vector similarity with keyword matching
    const vectorResults = await this.config.vectorStore.similaritySearch(query, 10);
    const keywordResults = await this.keywordSearch(query);
    
    return this.mergeAndDedup([...vectorResults, ...keywordResults]);
  }
}`,
  },
  {
    id: 'ml-training',
    title: 'Distributed ML Training with Kubernetes & Ray',
    description:
      'Scalable machine learning pipeline with distributed training, hyperparameter optimization, and model serving',
    icon: '/images/code/python.svg',
    iconAlt: 'Python',
    tags: ['Python', 'Kubernetes', 'Ray', 'MLOps', 'TensorFlow'],
    language: 'python',
    code: `import ray
from ray import tune
from ray.train import Trainer
from ray.serve import deployment
import kubernetes
from dataclasses import dataclass
from typing import Dict, Any, List
import torch
import torch.nn as nn
from torch.utils.data import DataLoader

@dataclass
class TrainingConfig:
    model_name: str
    batch_size: int
    learning_rate: float
    epochs: int
    num_workers: int
    use_gpu: bool = True

class DistributedMLPipeline:
    def __init__(self, config: TrainingConfig):
        self.config = config
        self.ray_cluster = None
        
    def setup_ray_cluster(self, kubernetes_config: Dict[str, Any]):
        """Initialize Ray cluster on Kubernetes"""
        ray.init(
            address="ray://head-service:10001",
            runtime_env={
                "pip": ["torch", "transformers", "datasets"],
                "env_vars": {"CUDA_VISIBLE_DEVICES": "0,1,2,3"}
            }
        )
        
    @ray.remote(num_gpus=1)
    class ModelTrainer:
        def __init__(self, config: TrainingConfig):
            self.config = config
            self.model = self.build_model()
            
        def build_model(self) -> nn.Module:
            # Custom model architecture
            return nn.Sequential(
                nn.Linear(768, 512),
                nn.ReLU(),
                nn.Dropout(0.1),
                nn.Linear(512, 256),
                nn.ReLU(),
                nn.Linear(256, self.config.num_classes)
            )
            
        def train_epoch(self, dataloader: DataLoader) -> Dict[str, float]:
            self.model.train()
            total_loss = 0.0
            
            for batch_idx, (data, target) in enumerate(dataloader):
                if self.config.use_gpu:
                    data, target = data.cuda(), target.cuda()
                    
                self.optimizer.zero_grad()
                output = self.model(data)
                loss = self.criterion(output, target)
                loss.backward()
                self.optimizer.step()
                
                total_loss += loss.item()
                
            return {"loss": total_loss / len(dataloader)}
            
    def hyperparameter_optimization(self):
        """Run distributed hyperparameter tuning"""
        config = {
            "learning_rate": tune.loguniform(1e-5, 1e-1),
            "batch_size": tune.choice([16, 32, 64, 128]),
            "dropout_rate": tune.uniform(0.1, 0.5),
        }
        
        tuner = tune.Tuner(
            self.trainable_function,
            param_space=config,
            tune_config=tune.TuneConfig(
                metric="accuracy",
                mode="max",
                num_samples=50,
            ),
            run_config=train.RunConfig(
                storage_path="/shared/ray-results",
                checkpoint_config=train.CheckpointConfig(
                    checkpoint_score_attribute="accuracy",
                    num_to_keep=3,
                ),
            ),
        )
        
        results = tuner.fit()
        return results.get_best_result()
        
    @deployment(num_replicas=3, ray_actor_options={"num_gpus": 0.5})
    class ModelServer:
        def __init__(self, model_path: str):
            self.model = torch.load(model_path)
            self.model.eval()
            
        async def predict(self, data: Dict[str, Any]) -> Dict[str, Any]:
            with torch.no_grad():
                prediction = self.model(torch.tensor(data["input"]))
                return {
                    "prediction": prediction.tolist(),
                    "confidence": torch.softmax(prediction, dim=-1).max().item()
                }`,
  },
  {
    id: 'microservices',
    title: 'Event-Driven Microservices with CQRS & Event Sourcing',
    description:
      'Scalable microservices architecture using CQRS, Event Sourcing, and distributed event streaming with Kafka',
    icon: '/images/code/nodejs.svg',
    iconAlt: 'Node.js',
    tags: ['Node.js', 'CQRS', 'Event Sourcing', 'Kafka', 'Docker'],
    language: 'javascript',
    code: `// Event-Driven Microservices Architecture
const { Kafka } = require('kafkajs');
const { EventStore } = require('@eventstore/db-client');
const express = require('express');
const { v4: uuidv4 } = require('uuid');

class EventSourcingService {
  constructor(eventStore, kafka) {
    this.eventStore = eventStore;
    this.kafka = kafka;
    this.producer = kafka.producer();
    this.consumer = kafka.consumer({ groupId: 'event-sourcing-group' });
  }

  async publishEvent(aggregateId, eventType, eventData) {
    const event = {
      id: uuidv4(),
      aggregateId,
      eventType,
      eventData,
      timestamp: new Date().toISOString(),
      version: await this.getNextVersion(aggregateId)
    };

    // Store in Event Store
    await this.eventStore.appendToStream(
      \`aggregate-\${aggregateId}\`,
      event
    );

    // Publish to Kafka for downstream services
    await this.producer.send({
      topic: 'domain-events',
      messages: [{
        key: aggregateId,
        value: JSON.stringify(event),
        headers: {
          eventType: eventType,
          aggregateId: aggregateId
        }
      }]
    });

    return event;
  }

  async getAggregateHistory(aggregateId) {
    const events = this.eventStore.readStream(
      \`aggregate-\${aggregateId}\`,
      { direction: 'forwards', fromRevision: 'start' }
    );

    const history = [];
    for await (const resolvedEvent of events) {
      history.push(resolvedEvent.event);
    }
    return history;
  }
}

// CQRS Command Handler
class CommandHandler {
  constructor(eventSourcingService) {
    this.eventService = eventSourcingService;
  }

  async handleCreateUser(command) {
    const { userId, email, name } = command;
    
    // Business logic validation
    if (!this.isValidEmail(email)) {
      throw new Error('Invalid email format');
    }

    // Publish domain event
    return await this.eventService.publishEvent(
      userId,
      'UserCreated',
      { email, name, createdAt: new Date().toISOString() }
    );
  }

  async handleUpdateUserProfile(command) {
    const { userId, profile } = command;
    
    // Load aggregate history for validation
    const history = await this.eventService.getAggregateHistory(userId);
    const userAggregate = this.rebuildUserAggregate(history);
    
    if (!userAggregate.isActive) {
      throw new Error('Cannot update inactive user');
    }

    return await this.eventService.publishEvent(
      userId,
      'UserProfileUpdated',
      { profile, updatedAt: new Date().toISOString() }
    );
  }
}`,
  },
  {
    id: 'gpu-vision',
    title: 'GPU-Accelerated Computer Vision Pipeline',
    description:
      'High-performance computer vision system with CUDA acceleration, real-time object detection, and distributed inference',
    icon: '/images/code/python.svg',
    iconAlt: 'Python',
    tags: ['Python', 'CUDA', 'OpenCV', 'TensorRT', 'PyTorch'],
    language: 'python',
    code: `import torch
import torch.nn as nn
import torchvision.transforms as transforms
import cv2
import numpy as np
from typing import List, Tuple, Dict
import tensorrt as trt
import pycuda.driver as cuda
import threading
from queue import Queue
from dataclasses import dataclass

@dataclass
class DetectionResult:
    bbox: Tuple[int, int, int, int]  # x, y, w, h
    confidence: float
    class_id: int
    class_name: str

class GPUVisionPipeline:
    def __init__(self, model_path: str, use_tensorrt: bool = True):
        self.device = torch.device('cuda' if torch.cuda.is_available() else 'cpu')
        self.use_tensorrt = use_tensorrt
        self.model = self.load_model(model_path)
        self.preprocess = self.setup_preprocessing()
        self.frame_queue = Queue(maxsize=30)
        self.result_queue = Queue(maxsize=30)
        
    def load_model(self, model_path: str):
        if self.use_tensorrt:
            return self.load_tensorrt_engine(model_path)
        else:
            model = torch.load(model_path, map_location=self.device)
            model.eval()
            return model
            
    def load_tensorrt_engine(self, engine_path: str):
        """Load optimized TensorRT engine for faster inference"""
        TRT_LOGGER = trt.Logger(trt.Logger.WARNING)
        
        with open(engine_path, 'rb') as f:
            engine_data = f.read()
            
        runtime = trt.Runtime(TRT_LOGGER)
        engine = runtime.deserialize_cuda_engine(engine_data)
        context = engine.create_execution_context()
        
        return context
        
    def setup_preprocessing(self):
        return transforms.Compose([
            transforms.ToPILImage(),
            transforms.Resize((640, 640)),
            transforms.ToTensor(),
            transforms.Normalize(
                mean=[0.485, 0.456, 0.406],
                std=[0.229, 0.224, 0.225]
            )
        ])
        
    def preprocess_frame(self, frame: np.ndarray) -> torch.Tensor:
        """GPU-accelerated preprocessing"""
        # Convert BGR to RGB
        frame_rgb = cv2.cvtColor(frame, cv2.COLOR_BGR2RGB)
        
        # Apply transforms and move to GPU
        tensor = self.preprocess(frame_rgb).unsqueeze(0)
        return tensor.to(self.device, non_blocking=True)
        
    @torch.no_grad()
    def detect_objects(self, frame: np.ndarray) -> List[DetectionResult]:
        """Perform object detection on a single frame"""
        input_tensor = self.preprocess_frame(frame)
        
        if self.use_tensorrt:
            outputs = self.tensorrt_inference(input_tensor)
        else:
            outputs = self.model(input_tensor)
            
        return self.postprocess_detections(outputs, frame.shape)
        
    def tensorrt_inference(self, input_tensor: torch.Tensor):
        """Optimized TensorRT inference"""
        # Allocate GPU memory for inputs and outputs
        inputs, outputs, bindings, stream = self.allocate_buffers()
        
        # Copy input data to GPU
        cuda.memcpy_htod_async(inputs[0].device, input_tensor.cpu().numpy(), stream)
        
        # Run inference
        self.model.execute_async_v2(bindings=bindings, stream_handle=stream.handle)
        
        # Copy output back to host
        cuda.memcpy_dtoh_async(outputs[0].host, outputs[0].device, stream)
        stream.synchronize()
        
        return torch.from_numpy(outputs[0].host).cuda()
        
    def real_time_processing(self, video_source: str = 0):
        """Real-time video processing with multi-threading"""
        cap = cv2.VideoCapture(video_source)
        cap.set(cv2.CAP_PROP_BUFFERSIZE, 1)  # Reduce latency
        
        # Start processing threads
        processing_thread = threading.Thread(target=self.process_frames)
        processing_thread.start()
        
        fps_counter = 0
        start_time = cv2.getTickCount()
        
        while True:
            ret, frame = cap.read()
            if not ret:
                break
                
            # Add frame to processing queue (non-blocking)
            if not self.frame_queue.full():
                self.frame_queue.put(frame)
                
            # Get processed results
            if not self.result_queue.empty():
                processed_frame, detections = self.result_queue.get()
                
                # Draw bounding boxes
                annotated_frame = self.draw_detections(processed_frame, detections)
                
                # Calculate FPS
                fps_counter += 1
                if fps_counter % 30 == 0:
                    end_time = cv2.getTickCount()
                    fps = 30 / ((end_time - start_time) / cv2.getTickFrequency())
                    print(f"FPS: {fps:.2f}")
                    start_time = end_time
                
                cv2.imshow('GPU Vision Pipeline', annotated_frame)
                
            if cv2.waitKey(1) & 0xFF == ord('q'):
                break
                
        cap.release()
        cv2.destroyAllWindows()
        
    def process_frames(self):
        """Background thread for GPU processing"""
        while True:
            if not self.frame_queue.empty():
                frame = self.frame_queue.get()
                detections = self.detect_objects(frame)
                
                if not self.result_queue.full():
                    self.result_queue.put((frame, detections))`,
  },
];

